import Transition from "@shared/core/Transition";
import { useEffect, useRef, useState } from "react";

type Difficulty = "easy" | "medium" | "hard";

interface DifficultyDropdownProps {
  align?: "left" | "right";
  selectedDifficulty: Difficulty | null;
  onChange: (difficulty: Difficulty) => void;
  onClose?: () => void;
  placeholder?: string;
}

export default function DifficultyDropdown({
  align = "left",
  selectedDifficulty,
  onChange,
  onClose,
  placeholder = "Select Difficulty",
}: DifficultyDropdownProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const dropdown = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        dropdown.current.contains(target as Node) ||
        trigger.current?.contains(target as Node)
      )
        return;
      setDropdownOpen(false);
      onClose?.();
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [onClose]);

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
      onClose?.();
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen, onClose]);

  // Get difficulty display name
  const getDifficultyDisplayName = (difficulty: Difficulty): string => {
    switch (difficulty) {
      case "easy":
        return "Easy";
      case "medium":
        return "Medium";
      case "hard":
        return "Hard";
      default:
        return "";
    }
  };

  // Get difficulty color class
  const getDifficultyColorClass = (difficulty: Difficulty): string => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "hard":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="relative">
      <button
        ref={trigger}
        className="btn bg-white dark:bg-gray-700 border-slate-200 dark:border-slate-700 hover:border-slate-300 text-slate-500 hover:text-slate-600 flex items-center"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="flex items-center">
          {selectedDifficulty ? (
            <>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${getDifficultyColorClass(
                  selectedDifficulty
                )}`}
              >
                {getDifficultyDisplayName(selectedDifficulty)}
              </span>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </span>
        <svg
          className="w-5 h-5 ml-2 -mr-1 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <Transition
        show={dropdownOpen}
        tag="div"
        className={`absolute z-10 w-full overflow-y-auto bg-white rounded-md shadow-lg mt-1 ${
          align === "right" ? "right-0" : "left-0"
        }`}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="py-1"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
        >
          {["easy", "medium", "hard"].map((difficulty) => (
            <button
              key={difficulty}
              className={`flex items-center w-full px-4 py-2 text-sm hover:bg-gray-100 ${
                selectedDifficulty === difficulty ? "bg-gray-50" : ""
              }`}
              role="menuitem"
              onClick={() => {
                onChange(difficulty as Difficulty);
                setDropdownOpen(false);
              }}
            >
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${getDifficultyColorClass(
                  difficulty as Difficulty
                )}`}
              >
                {getDifficultyDisplayName(difficulty as Difficulty)}
              </span>
            </button>
          ))}
        </div>
      </Transition>
    </div>
  );
}
